export interface TripPoolItem {
  id: string;
  type: "experience" | "farm" | "culture";
  title: string;
  meta: string;
  icon: string;
  duration: string;
  href: string;
  /** Place used to centre the itinerary map preview on the print page. */
  mapQuery: string;
}

/**
 * Everything a visitor can drop into their own itinerary.
 * type "experience" -> links to a detail page
 * type "farm"       -> links back to the farms page
 * type "culture"    -> Konaseema guide essentials
 */
export const tripPool: TripPoolItem[] = [
  {
    id: "exp-farmer-for-a-day",
    mapQuery: "Konaseema, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Farmer for a Day",
    meta: "Full day · Konaseema farmlands",
    icon: "ri-plant-line",
    duration: "8 hrs",
    href: "/experiences/farmer-for-a-day",
  },
  {
    id: "exp-soil-to-plate",
    mapQuery: "Ravulapalem, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Soil to Plate",
    meta: "Half day · Farm & village kitchen",
    icon: "ri-restaurant-2-line",
    duration: "5 hrs",
    href: "/experiences/soil-to-plate",
  },
  {
    id: "exp-village-life",
    mapQuery: "Amalapuram, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Village Life Experience",
    meta: "Full day · Villages near Amalapuram",
    icon: "ri-home-4-line",
    duration: "7 hrs",
    href: "/experiences/village-life-experience",
  },
  {
    id: "exp-orchard",
    mapQuery: "Mummidivaram, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Orchard & Plantation",
    meta: "Half day · Groves & mango orchards",
    icon: "ri-leaf-line",
    duration: "4 hrs",
    href: "/experiences/orchard-plantation-experiences",
  },
  {
    id: "exp-family-farm",
    mapQuery: "Kothapeta, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Family Farm Experience",
    meta: "Half day · Family friendly",
    icon: "ri-home-heart-line",
    duration: "4.5 hrs",
    href: "/experiences/family-farm-experiences",
  },
  {
    id: "exp-rural-culture",
    mapQuery: "P. Gannavaram, East Godavari, Andhra Pradesh, India",
    type: "experience",
    title: "Rural Culture & Traditions",
    meta: "Half–full day · Crafts & festivals",
    icon: "ri-artboard-line",
    duration: "5 hrs",
    href: "/experiences/rural-culture-traditions",
  },
  {
    id: "farm-rajanna",
    mapQuery: "Konaseema, East Godavari, Andhra Pradesh, India",
    type: "farm",
    title: "Rajanna's Paddy Farm",
    meta: "Paddy & coconut · 6 acres",
    icon: "ri-seedling-line",
    duration: "2 hrs",
    href: "/farms",
  },
  {
    id: "farm-surya",
    mapQuery: "Mummidivaram, East Godavari, Andhra Pradesh, India",
    type: "farm",
    title: "Surya Prakash's Coconut Grove",
    meta: "Coconut · 4 acres",
    icon: "ri-plant-fill",
    duration: "2 hrs",
    href: "/farms",
  },
  {
    id: "farm-srinivas",
    mapQuery: "Ramachandrapuram, East Godavari, Andhra Pradesh, India",
    type: "farm",
    title: "Srinivas Reddy's Mango Orchard",
    meta: "Mango · 7 acres",
    icon: "ri-tree-line",
    duration: "2 hrs",
    href: "/farms",
  },
  {
    id: "farm-lakshmi",
    mapQuery: "Ravulapalem, East Godavari, Andhra Pradesh, India",
    type: "farm",
    title: "Lakshmi Devi's Vegetable Garden",
    meta: "Vegetables & flowers · 2.5 acres",
    icon: "ri-plant-line",
    duration: "2 hrs",
    href: "/farms",
  },
  {
    id: "culture-boating",
    mapQuery: "Konaseema, East Godavari, Andhra Pradesh, India",
    type: "culture",
    title: "Backwater Boating",
    meta: "Konaseema canals · Fresh morning",
    icon: "ri-ship-2-line",
    duration: "2 hrs",
    href: "/konaseema",
  },
  {
    id: "culture-temple",
    mapQuery: "Ainavilli, East Godavari, Andhra Pradesh, India",
    type: "culture",
    title: "Temple & Heritage Visit",
    meta: "Craft villages & temple towns",
    icon: "ri-ancient-gate-line",
    duration: "2 hrs",
    href: "/konaseema",
  },
  {
    id: "culture-food",
    mapQuery: "Amalapuram, East Godavari, Andhra Pradesh, India",
    type: "culture",
    title: "Andhra Village Food Trail",
    meta: "Local curries, fish & coconut dishes",
    icon: "ri-bowl-line",
    duration: "2 hrs",
    href: "/konaseema",
  },
  {
    id: "culture-market",
    mapQuery: "Kothapeta, East Godavari, Andhra Pradesh, India",
    type: "culture",
    title: "Village Market Morning",
    meta: "Weekly markets & fresh produce",
    icon: "ri-store-2-line",
    duration: "2 hrs",
    href: "/konaseema",
  },
];

export const tripPoolFilters = [
  { id: "all", label: "Everything" },
  { id: "experience", label: "Experiences" },
  { id: "farm", label: "Farms" },
  { id: "culture", label: "Konaseema" },
];

export interface TripStop {
  time: string;
  title: string;
  text: string;
  icon: string;
  href?: string;
}

export interface TripPresetDay {
  label: string;
  theme: string;
  stops: TripStop[];
}

export interface TripPreset {
  id: string;
  title: string;
  duration: string;
  audience: string;
  summary: string;
  image: string;
  days: TripPresetDay[];
}

export const tripPresets: TripPreset[] = [
  {
    id: "classic-day",
    title: "The Classic Day Trip",
    duration: "1 day",
    audience: "First-time visitors",
    summary:
      "The perfect introduction to the delta — sunrise in the fields, a farm lunch on banana leaf and a morning boat ride.",
    image:
      "https://readdy.ai/api/search-image?query=Fresh%20sunrise%20over%20green%20paddy%20fields%20in%20the%20Godavari%20delta%20with%20a%20farmer%20silhouette%20and%20coconut%20palms%20in%20the%20mist%2C%20painterly%20stylized%20lush%20green%20tones%2C%20serene%20inviting%20rural%20Indian%20landscape%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=trip-preset-classic&orientation=landscape",
    days: [
      {
        label: "Day 1",
        theme: "Fields, Village & Water",
        stops: [
          {
            time: "6:30 AM",
            title: "Sunrise field walk & farm chai",
            text: "Arrive early, share chai with a farming family and walk the paddy fields as the sun comes up.",
            icon: "ri-sun-line",
            href: "/experiences/farmer-for-a-day",
          },
          {
            time: "9:00 AM",
            title: "Irrigation & paddy trail",
            text: "Learn how water moves through the delta's old channels and how a single grain is grown.",
            icon: "ri-water-flash-line",
            href: "/experiences/farmer-for-a-day",
          },
          {
            time: "11:30 AM",
            title: "Village life walk",
            text: "Stroll the lanes with a local host, meet artisans and hear stories under the banyan tree.",
            icon: "ri-home-4-line",
            href: "/experiences/village-life-experience",
          },
          {
            time: "1:00 PM",
            title: "Traditional farm lunch",
            text: "A leisurely Andhra meal served on a banana leaf, cooked with ingredients from the fields.",
            icon: "ri-restaurant-2-line",
            href: "/experiences/soil-to-plate",
          },
          {
            time: "3:30 PM",
            title: "Coconut grove & tender coconut",
            text: "Wander a shaded grove, learn how coconuts are harvested and sip one fresh from the tree.",
            icon: "ri-plant-fill",
            href: "/experiences/orchard-plantation-experiences",
          },
          {
            time: "5:00 PM",
            title: "Morning backwater ride",
            text: "End the day drifting along the canals as the light turns gold — the delta at its best.",
            icon: "ri-ship-2-line",
            href: "/konaseema",
          },
        ],
      },
    ],
  },
  {
    id: "family-weekend",
    title: "Weekend Family Escape",
    duration: "2 days",
    audience: "Families with children",
    summary:
      "Two relaxed days built at a child's pace — soil, animals, harvesting and a farm-to-table cooking session everybody joins in.",
    image:
      "https://readdy.ai/api/search-image?query=Happy%20family%20with%20young%20children%20exploring%20a%20green%20farm%20at%20early%20morning%2C%20kids%20touching%20plants%20and%20laughing%2C%20fresh%20painterly%20green%20tones%2C%20joyful%20rural%20scene%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=trip-preset-family&orientation=landscape",
    days: [
      {
        label: "Day 1",
        theme: "Hands in the Soil",
        stops: [
          {
            time: "9:00 AM",
            title: "Welcome & farm animal meet",
            text: "Meet the calves, goats and hens that live on the farm — always the children's favourite start.",
            icon: "ri-emotion-happy-line",
            href: "/experiences/family-farm-experiences",
          },
          {
            time: "10:30 AM",
            title: "Plant a sapling together",
            text: "Every family plants and names a sapling, then takes home a little seedling to grow.",
            icon: "ri-seedling-line",
            href: "/experiences/family-farm-experiences",
          },
          {
            time: "12:30 PM",
            title: "Farm picnic lunch",
            text: "A wholesome meal on a woven mat under the trees, with plenty of room for the kids to run.",
            icon: "ri-restaurant-2-line",
            href: "/experiences/family-farm-experiences",
          },
          {
            time: "3:00 PM",
            title: "Village evening walk",
            text: "A gentle wander through the lanes with a fruit stop and a look at the temple.",
            icon: "ri-home-4-line",
            href: "/experiences/village-life-experience",
          },
        ],
      },
      {
        label: "Day 2",
        theme: "From Soil to Plate",
        stops: [
          {
            time: "8:30 AM",
            title: "Harvest the day's vegetables",
            text: "Pick the very vegetables that will become lunch — kids love pulling carrots and greens.",
            icon: "ri-plant-line",
            href: "/experiences/soil-to-plate",
          },
          {
            time: "10:00 AM",
            title: "Family cooking class",
            text: "Cook a traditional recipe together with a local cook, step by step, at a relaxed pace.",
            icon: "ri-restaurant-2-line",
            href: "/experiences/soil-to-plate",
          },
          {
            time: "12:30 PM",
            title: "Eat what you grew",
            text: "Sit down to the farm-to-table meal the whole family helped create.",
            icon: "ri-bowl-line",
            href: "/experiences/soil-to-plate",
          },
          {
            time: "3:00 PM",
            title: "Market & craft stop",
            text: "Browse a weekly market for fresh produce and pick up a small handmade keepsake.",
            icon: "ri-store-2-line",
            href: "/konaseema",
          },
        ],
      },
    ],
  },
  {
    id: "delta-immersion",
    title: "The Delta Immersion",
    duration: "3 days",
    audience: "Slow travellers & photographers",
    summary:
      "Three unhurried days that weave farms, villages, backwaters and culture together for a true sense of Konaseema.",
    image:
      "https://readdy.ai/api/search-image?query=Calm%20Konaseema%20backwater%20canal%20at%20dawn%20with%20a%20wooden%20boat%20and%20mist%20over%20coconut%20groves%2C%20painterly%20stylized%20earthy%20green%20tones%2C%20peaceful%20immersive%20rural%20India%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=trip-preset-immersion&orientation=landscape",
    days: [
      {
        label: "Day 1",
        theme: "Arrive & Settle In",
        stops: [
          {
            time: "12:00 PM",
            title: "Arrive & village welcome",
            text: "Settle into your stay and take a slow welcome walk through the village lanes.",
            icon: "ri-home-4-line",
            href: "/experiences/village-life-experience",
          },
          {
            time: "4:00 PM",
            title: "Canal-side sunset",
            text: "Watch the delta light change over the water with a fresh tender coconut in hand.",
            icon: "ri-ship-2-line",
            href: "/konaseema",
          },
          {
            time: "7:00 PM",
            title: "Folk evening",
            text: "Local folk songs and storytelling over a traditional village dinner.",
            icon: "ri-music-2-line",
            href: "/experiences/rural-culture-traditions",
          },
        ],
      },
      {
        label: "Day 2",
        theme: "A Full Day in the Fields",
        stops: [
          {
            time: "6:30 AM",
            title: "Farmer for a Day",
            text: "Spend the whole day working alongside a farming family, following the real work of the season.",
            icon: "ri-plant-line",
            href: "/experiences/farmer-for-a-day",
          },
          {
            time: "3:00 PM",
            title: "Orchard & grove trail",
            text: "Move on to a mango orchard and coconut grove, tasting fruit picked by your own hands.",
            icon: "ri-leaf-line",
            href: "/experiences/orchard-plantation-experiences",
          },
        ],
      },
      {
        label: "Day 3",
        theme: "Food, Craft & Farewell",
        stops: [
          {
            time: "8:30 AM",
            title: "Soil to Plate cooking",
            text: "Harvest, cook and share a traditional meal to understand the full journey of your food.",
            icon: "ri-restaurant-2-line",
            href: "/experiences/soil-to-plate",
          },
          {
            time: "12:00 PM",
            title: "Temple & heritage visit",
            text: "Discover centuries-old temples and the stories that shaped the delta's villages.",
            icon: "ri-ancient-gate-line",
            href: "/konaseema",
          },
          {
            time: "3:00 PM",
            title: "Farewell & depart",
            text: "A last market stop and a heartfelt goodbye before the road home.",
            icon: "ri-road-map-line",
            href: "/konaseema",
          },
        ],
      },
    ],
  },
];